<script setup lang="ts">
import LoaderCenter from '@/components/common/feedback/LoaderCenter.vue'

const props = defineProps({
  type: {
    type: String as () => 'button' | 'submit' | 'reset' | undefined,
    default: 'submit',
  },
  text: {
    type: String,
    required: true,
  },
  variant: {
    type: String,
    validator: (value: string) =>
      [
        'primary',
        'secondary',
        'accent',
        'hub',
        'ghost',
        'outline',
        'soft-primary',
        'soft-secondary',
        'soft-accent',
        'soft-error',
        'neutral',
        'default',
        'error',
        'warning',
        'success',
      ].includes(value),
    default: 'default',
  },
  icon: {
    type: [Object, Function],
    default: null,
  },
  loader: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: 'sm',
  },
  sizeIcon: {
    type: String,
    default: '4',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  rounded: {
    type: Boolean,
    default: false,
  },
})
defineEmits(['click'])

function variantLoader() {
  return props.variant === 'error' || props.variant === 'primary' || props.variant === 'success' || props.variant === 'warning' ? 'white' : 'primary'
}
</script>

<template>
  <button v-if="loader" :class="'btn btn-' + variant + ' btn-' + size" :disabled="disabled" :type="type">
    <LoaderCenter :color="variantLoader()" />
  </button>
  <button
    v-else
    @click="$emit('click')"
    :disabled="disabled"
    :type="type"
    :class="{
      'btn flex shadow-none': true,
      'border-none': variant !== 'outline',
      'btn-primary': variant === 'primary',
      'btn-secondary': variant === 'secondary',
      'btn-accent': variant === 'accent',
      'btn-hub': variant === 'hub',
      'btn-error text-base-100': variant === 'error',
      'btn-ghost': variant === 'ghost',
      'btn-outline': variant === 'outline',
      'btn-soft btn-primary': variant === 'soft-primary',
      'btn-soft btn-secondary': variant === 'soft-secondary',
      'btn-soft btn-accent': variant === 'soft-accent',
      'btn-soft btn-error': variant === 'soft-error',
      'btn-neutral': variant === 'neutral',
      'btn-warning text-warning-content': variant === 'warning',
      'btn-success text-success-content': variant === 'success',
      'btn-xs': size === 'xs',
      'btn-sm': size === 'sm',
      'rounded-full': rounded,
      'hover:bg-primary hover:text-primary-content': variant === 'default',
    }"
  >
    <div class="flex items-center max-w-56">
      <component v-if="icon" :is="icon" :class="'shrink-0 h-' + sizeIcon + ' w-' + sizeIcon" />
      <span v-if="text" :class="['text-left truncate', icon ? 'ml-2' : '']">{{ text }}</span>
      <slot />
    </div>
  </button>
</template>

<style scoped>
/* Variante "hub" : CTA teinté de la couleur de la section active (--hub-accent),
   posée par ApyLayout selon le hub. Le texte utilise --hub-accent-content (lisible
   AA sur l'accent). Hors shell V2 (vars absentes), la chaîne de fallback CSS résout
   jusqu'au corail/pétrole en dur. */
.btn-hub {
  background-color: var(--hub-accent, var(--apy-s2, #fd6c56));
  color: var(--hub-accent-content, var(--apy-p1, #033f52));
  border-color: transparent;
}
.btn-hub:hover:not(:disabled) {
  background-color: color-mix(in oklab, var(--hub-accent, #fd6c56) 88%, #000);
}
</style>
